import type { CogoElement, CogoLayout } from "../core/types"
import { spacingToken } from "./design-tokens"

type UnknownRecord = Record<string, unknown>

const BREAKPOINTS = new Set(["sm", "md", "lg", "xl", "2xl"])
const ALIGN_VALUES = new Set(["start", "center", "end", "stretch", "baseline"])
const JUSTIFY_VALUES = new Set(["start", "center", "end", "between", "around", "evenly"])
const DENSITY_VALUES = new Set(["compact", "default", "comfortable"])
const SURFACE_VALUES = new Set(["canvas", "card", "component", "muted", "panel", "popover"])
const RADIUS_VALUES = new Set(["none", "sm", "md", "lg", "xl", "2xl", "full"])
const SHADOW_VALUES = new Set(["none", "sm", "md", "lg", "xl"])
const TONE_VALUES = new Set(["default", "primary", "secondary", "accent", "muted", "destructive"])

/**
 * Converts renderer-neutral typed design/layout facts into Cogo-owned
 * structural classes. This is the Web renderer bridge for the same facts that
 * RenderPack carries as layoutOps/designOps; it deliberately avoids arbitrary
 * inline style objects and arbitrary utility parsing.
 */
export function applyTypedDesignContract(element: CogoElement): CogoElement {
  const raw = element as unknown as UnknownRecord
  const layout = readRecord(raw.layout)
  const design = readRecord(raw.design)
  const classes = [
    ...layoutContractClasses(layout),
    ...designContractClasses(raw, design),
  ]

  const normalizedLayout = normalizeLayoutKind(element.layout)
  if (classes.length === 0 && normalizedLayout === element.layout) return element

  return {
    ...element,
    layout: normalizedLayout,
    className: appendContractClasses(classes, element.className),
  }
}

function layoutContractClasses(layout: UnknownRecord | undefined): string[] {
  if (!layout) return []
  const classes: string[] = []
  const kind = readString(layout.kind) ?? readString(layout.type)
  const direction = readString(layout.direction)
  const align = readString(layout.align) ?? readString(layout.alignItems)
  const justify = readString(layout.justify) ?? readString(layout.justifyContent)
  const gap = layout.gap
  const columns = layout.columns

  const layoutClass = layoutKindClass(kind, direction)
  if (layoutClass) classes.push(layoutClass)
  if (layout.wrap === true) classes.push("cogo-layout-wrap")
  if (align && ALIGN_VALUES.has(align)) classes.push(`cogo-items-${align}`)
  if (justify && JUSTIFY_VALUES.has(justify)) classes.push(`cogo-justify-${justify}`)

  const gapClass = gapContractClass(gap)
  if (gapClass) classes.push(gapClass)

  const columnClass = columnsContractClass(columns)
  if (columnClass) classes.push(columnClass)

  for (const [breakpoint, overrides] of responsiveEntries(layout.responsive)) {
    const responsiveKind = readString(overrides.kind) ?? readString(overrides.type)
    const responsiveDirection = readString(overrides.direction)
    const responsiveLayoutClass = layoutKindClass(responsiveKind, responsiveDirection)
    if (responsiveLayoutClass) classes.push(responsiveClass(breakpoint, responsiveLayoutClass))

    const responsiveGap = gapContractClass(overrides.gap)
    if (responsiveGap) classes.push(responsiveClass(breakpoint, responsiveGap))

    const responsiveColumns = columnsContractClass(overrides.columns)
    if (responsiveColumns) classes.push(responsiveClass(breakpoint, responsiveColumns))
  }

  return classes
}

function designContractClasses(raw: UnknownRecord, design: UnknownRecord | undefined): string[] {
  const classes: string[] = []
  const density = readDesignString(raw, design, "density")
  const surface = readDesignString(raw, design, "surface")
  const radius = readDesignString(raw, design, "radius") ?? readDesignString(raw, design, "rounded")
  const shadow = readDesignString(raw, design, "shadow")
  const tone = readDesignString(raw, design, "tone")
  const styleName =
    readDesignString(raw, design, "style") ??
    readDesignString(raw, design, "styleScope") ??
    readDesignString(raw, design, "theme")

  if (density && DENSITY_VALUES.has(density)) classes.push(`cogo-density-${density}`)
  if (surface && SURFACE_VALUES.has(surface)) classes.push(`cogo-surface-${surface}`)
  if (radius && RADIUS_VALUES.has(radius)) classes.push(`cogo-radius-${radius}`)
  if (shadow && SHADOW_VALUES.has(shadow)) classes.push(`cogo-shadow-${shadow}`)
  if (tone && TONE_VALUES.has(tone)) classes.push(`cogo-tone-${tone}`)
  if (styleName && isSafeStyleScope(styleName)) classes.push(`style-${styleName}`)

  return classes
}

function normalizeLayoutKind(layout: CogoLayout | undefined): CogoLayout | undefined {
  const raw = layout as unknown as UnknownRecord | undefined
  if (!raw || raw.type) return layout

  const kind = readString(raw.kind)
  if (!kind) return layout

  if (kind === "grid") return { ...(layout as CogoLayout), type: "grid" }
  if (kind === "stack" || kind === "column") {
    return { ...(layout as CogoLayout), type: "flex", direction: "column" }
  }
  if (kind === "row" || kind === "inline" || kind === "flex") {
    return { ...(layout as CogoLayout), type: "flex", direction: "row" }
  }
  return layout
}

function layoutKindClass(kind: string | undefined, direction: string | undefined): string | null {
  if (kind === "grid") return "cogo-layout-grid"
  if (kind === "slot") return "cogo-layout-slot"
  if (kind === "stack" || kind === "column") return "cogo-layout-stack"
  if (kind === "row") return "cogo-layout-row"
  if (kind === "inline") return "cogo-layout-inline"
  if (kind === "flex" && direction === "column") return "cogo-layout-stack"
  if (kind === "flex") return "cogo-layout-row"
  return null
}

function gapContractClass(value: unknown): string | null {
  const token = contractSpacingToken(value)
  return token ? `cogo-gap-${token}` : null
}

function columnsContractClass(value: unknown): string | null {
  if (typeof value !== "number" && typeof value !== "string") return null
  const columns = Number(value)
  if (!Number.isInteger(columns) || columns < 1 || columns > 12) return null
  return `cogo-grid-cols-${columns}`
}

function responsiveClass(breakpoint: string, contractClass: string): string {
  return `cogo-${breakpoint}-${contractClass.replace(/^cogo-/, "")}`
}

function responsiveEntries(value: unknown): Array<[string, UnknownRecord]> {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      const record = readRecord(entry)
      const breakpoint = readString(record?.at) ?? readString(record?.breakpoint) ?? readString(record?.bp)
      return record && breakpoint && BREAKPOINTS.has(breakpoint)
        ? [[breakpoint, record] as [string, UnknownRecord]]
        : []
    })
  }

  const record = readRecord(value)
  if (!record) return []
  return Object.entries(record).flatMap(([breakpoint, overrides]) => {
    const overrideRecord = readRecord(overrides)
    return BREAKPOINTS.has(breakpoint) && overrideRecord
      ? [[breakpoint, overrideRecord] as [string, UnknownRecord]]
      : []
  })
}

function contractSpacingToken(value: unknown): string | null {
  if (typeof value !== "string" && typeof value !== "number") return null
  const token = spacingToken(value).replace(/\./g, "_")
  return /^-?[0-9]+(?:_[0-9]+)?$/.test(token) ? token : null
}

function readDesignString(raw: UnknownRecord, design: UnknownRecord | undefined, key: string): string | undefined {
  return readString(design?.[key]) ?? readString(raw[key])
}

function readRecord(value: unknown): UnknownRecord | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as UnknownRecord
    : undefined
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : undefined
}

function isSafeStyleScope(value: string): boolean {
  return /^[a-z][a-z0-9-]{0,31}$/.test(value)
}

function appendContractClasses(contractClasses: string[], existing: string | undefined): string {
  const out: string[] = []
  const seen = new Set<string>()
  for (const token of [...contractClasses, ...(existing ?? "").split(/\s+/)]) {
    if (!token || seen.has(token)) continue
    seen.add(token)
    out.push(token)
  }
  return out.join(" ")
}
